import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, LogOut, Mail, MailOpen, Trash2, Inbox } from "lucide-react";
import { http } from "@/lib/api";
import { Wordmark } from "@/components/site/Logo";

const TOPICS = { events: "Event production", training: "Professional training", business: "Business development", services: "Business services", partnership: "Partnership", other: "Other" };
const fmt = (iso) => new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });

async function authed(fn) {
  try {
    return await fn();
  } catch (e) {
    if (e?.response?.status !== 401) throw e;
    await http.post("/auth/refresh");
    return fn();
  }
}

const MessageDetail = ({ m, onBack, onToggle, onDelete }) => (
  <article data-testid="admin-message-detail" className="p-6 sm:p-10">
    <button onClick={onBack} data-testid="admin-detail-back" className="mb-6 flex items-center gap-2 text-sm text-zinc-400 lg:hidden"><ArrowLeft className="h-4 w-4" />Back</button>
    <p className="eyebrow">{TOPICS[m.topic] ?? m.topic} · {m.lang?.toUpperCase()} · {fmt(m.created_at)}</p>
    <h2 className="mt-4 font-display text-3xl font-semibold tracking-tight">{m.name}</h2>
    <p className="mt-1 text-zinc-300"><a href={`mailto:${m.email}`} className="underline decoration-signal underline-offset-4">{m.email}</a>{m.organisation ? ` · ${m.organisation}` : ""}</p>
    <p data-testid="admin-message-body" className="mt-8 whitespace-pre-wrap border-t border-white/10 pt-8 leading-relaxed text-zinc-200">{m.message}</p>
    <p className="mt-6 font-mono text-xs text-zinc-400">Privacy consent given: {fmt(m.consent_at)}</p>
    <div className="mt-8 flex flex-wrap gap-3">
      <a href={`mailto:${m.email}?subject=${encodeURIComponent("Re: your message to GridJob OÜ")}`} data-testid="admin-reply-link" className="rounded-full bg-signal px-5 py-2.5 text-sm font-medium text-ink">Reply by email</a>
      <button onClick={onToggle} data-testid="admin-toggle-read" className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm">{m.read ? <><Mail className="h-4 w-4" />Mark unread</> : <><MailOpen className="h-4 w-4" />Mark read</>}</button>
      <button onClick={onDelete} data-testid="admin-delete-message" className="flex items-center gap-2 rounded-full border border-red-400/40 px-5 py-2.5 text-sm text-red-300"><Trash2 className="h-4 w-4" />Delete</button>
    </div>
  </article>
);

export default function AdminInbox() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [selId, setSelId] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      const { data } = await authed(() => http.get("/admin/messages"));
      setItems(Array.isArray(data) ? data : []);
    } catch {
      navigate("/admin", { replace: true });
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const list = useMemo(() => (items ?? []).filter((m) => filter === "all" || !m.read), [items, filter]);
  const sel = (items ?? []).find((m) => m.id === selId);
  const unread = (items ?? []).filter((m) => !m.read).length;

  const update = (m) => setItems((xs) => xs.map((x) => (x.id === m.id ? m : x)));
  const setRead = async (m, read) => {
    const { data } = await authed(() => http.patch(`/admin/messages/${m.id}`, { read }));
    update(data);
  };
  const open = (m) => { setSelId(m.id); if (!m.read) setRead(m, true).catch(() => {}); };
  const remove = async (m) => {
    if (!window.confirm(`Delete the message from ${m.name}? This cannot be undone.`)) return;
    await authed(() => http.delete(`/admin/messages/${m.id}`));
    setItems((xs) => xs.filter((x) => x.id !== m.id));
    setSelId(null);
    toast.success("Message deleted");
  };
  const logout = async () => { await http.post("/auth/logout").catch(() => {}); navigate("/admin"); };

  return (
    <div className="min-h-screen bg-ink text-bone">
      <header className="border-b border-white/10">
        <div className="wrap flex h-16 items-center justify-between">
          <div className="flex items-center gap-4"><Wordmark /><span className="hidden font-mono text-xs uppercase tracking-[0.2em] text-zinc-400 sm:inline">Inbox</span></div>
          <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm"><LogOut className="h-4 w-4" />Sign out</button>
        </div>
      </header>
      <div className="wrap grid gap-0 py-8 lg:grid-cols-[400px_1fr]">
        <section className={`border border-white/10 ${sel ? "hidden lg:block" : ""}`}>
          <div className="flex items-center justify-between border-b border-white/10 p-4">
            <p data-testid="admin-unread-count" className="text-sm text-zinc-300">{items ? `${items.length} messages · ${unread} unread` : "Loading…"}</p>
            <div className="flex gap-1 font-mono text-xs">
              {["all", "unread"].map((f) => (
                <button key={f} data-testid={`admin-filter-${f}`} onClick={() => setFilter(f)} className={`rounded-full px-3 py-1 uppercase ${filter === f ? "bg-bone text-ink" : "text-zinc-400"}`}>{f}</button>
              ))}
            </div>
          </div>
          {items && list.length === 0 && (
            <div data-testid="admin-empty" className="flex flex-col items-center gap-3 p-12 text-center text-zinc-400"><Inbox className="h-8 w-8" />No messages yet.</div>
          )}
          <ul data-testid="admin-message-list">
            {list.map((m) => (
              <li key={m.id}>
                <button onClick={() => open(m)} data-testid={`admin-message-${m.id}`} className={`block w-full border-b border-white/10 p-4 text-left transition-colors hover:bg-ink-2 ${selId === m.id ? "bg-ink-2" : ""}`}>
                  <div className="flex items-center justify-between gap-3">
                    <span className={`truncate ${m.read ? "text-zinc-300" : "font-semibold text-bone"}`}>{!m.read && <span className="mr-2 inline-block h-2 w-2 rounded-full bg-signal" />}{m.name}</span>
                    <span className="shrink-0 font-mono text-xs text-zinc-400">{new Date(m.created_at).toLocaleDateString("en-GB")}</span>
                  </div>
                  <p className="mt-1 truncate text-sm text-zinc-400">{TOPICS[m.topic] ?? m.topic} — {m.message}</p>
                </button>
              </li>
            ))}
          </ul>
        </section>
        <section className={`border border-white/10 lg:border-l-0 ${sel ? "" : "hidden lg:block"}`}>
          {sel ? (
            <MessageDetail m={sel} onBack={() => setSelId(null)} onToggle={() => setRead(sel, !sel.read)} onDelete={() => remove(sel)} />
          ) : (
            <div className="grid h-full min-h-[300px] place-items-center p-10 text-zinc-400">Select a message to read it.</div>
          )}
        </section>
      </div>
    </div>
  );
}
